import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi, publicApi } from '../services/api';
import { useAuthStore } from '../store/auth.store';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { Loader2, Mail, Lock, ArrowRight, ShieldCheck, ArrowLeft, CheckCircle2, Fingerprint, Eye, EyeOff } from 'lucide-react';
import { browserSupportsWebAuthn, loginWithPasskey } from '../services/webauthn';
import { usePasswordReveal } from '../hooks/usePasswordReveal';
import { isSupportedLocale } from '../i18n';

// Force i18next to the user's stored preference right after login. Without
// this, the locale falls back to whatever was cached in localStorage, which
// can drift from the DB value (e.g. cross-device login, language change that
// failed to persist server-side) and leave the dispatcher in the wrong locale
// until they refresh and Layout's sync effect runs.
async function applyUserLocale(i18n: { resolvedLanguage?: string; changeLanguage: (l: string) => Promise<unknown> }, language?: string | null) {
  if (language && isSupportedLocale(language) && i18n.resolvedLanguage !== language) {
    try {
      await i18n.changeLanguage(language);
    } catch {
      // Best-effort — ignore failures, Layout's effect will retry.
    }
  }
}

interface AppearanceSettings {
  companyName: string;
  companyTagline?: string;
  logo?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  showRegistration?: boolean;
}

const defaultAppearance: AppearanceSettings = {
  companyName: 'TrackFleet',
  companyTagline: 'GPS Fleet Tracking',
  primaryColor: '#1f8da1',
  secondaryColor: '#6366f1',
  accentColor: '#f59e0b',
};

const APPEARANCE_CACHE_KEY = 'trackfleet:appearance';

function loadCachedAppearance(): AppearanceSettings {
  try {
    const raw = localStorage.getItem(APPEARANCE_CACHE_KEY);
    if (raw) return { ...defaultAppearance, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return defaultAppearance;
}

export default function Login() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const { isDesktop } = useBreakpoint();
  const { t, i18n } = useTranslation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const passwordReveal = usePasswordReveal(password, setPassword);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [appearance, setAppearance] = useState<AppearanceSettings>(loadCachedAppearance);

  // Forgot password state
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');

  // 2FA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaUserId, setMfaUserId] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaEmail, setMfaEmail] = useState('');
  const [, setMfaMethod] = useState<string>('TOTP');
  const [mfaCode, setMfaCode] = useState(['', '', '', '', '', '']);
  const mfaInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [isDuoRedirecting, setIsDuoRedirecting] = useState(false);

  // Passkey state
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [supportsPasskey, setSupportsPasskey] = useState(false);

  useEffect(() => {
    setSupportsPasskey(browserSupportsWebAuthn());
  }, []);

  const handlePasskeyLogin = async () => {
    try {
      setPasskeyLoading(true);
      setError('');

      // 1. Begin — get challenge from backend (discoverable mode, no email)
      const { data: beginData } = await authApi.passkeyLoginBegin();

      // 2. Browser ceremony (Face ID / Touch ID / Windows Hello)
      const assertion = await loginWithPasskey(beginData.options);

      // 3. Send assertion to backend
      const { data } = await authApi.passkeyLoginFinish(beginData.challengeToken, assertion);

      // 4. Store auth and redirect
      const { user, accessToken } = data;
      setAuth(user, accessToken);
      await applyUserLocale(i18n, user.language);

      if (user.role !== 'SUPER_ADMIN' && !user.cguAccepted) {
        navigate('/accept-cgu');
      } else if (user.role !== 'SUPER_ADMIN' && user.mustSetup2FA) {
        navigate('/setup-2fa');
      } else {
        navigate(isDesktop ? '/dashboard' : '/map');
      }
    } catch (err: unknown) {
      const error = err as { name?: string; response?: { data?: { error?: string } } };
      // Ignore user cancellation
      if (error.name === 'AbortError' || error.name === 'NotAllowedError') {
        return;
      }
      setError(error.response?.data?.error || t('auth.passkey_failed'));
    } finally {
      setPasskeyLoading(false);
    }
  };

  useEffect(() => {
    publicApi.getAppearance()
      .then((data) => {
        const merged = { ...defaultAppearance, ...data };
        setAppearance(merged);
        try { localStorage.setItem(APPEARANCE_CACHE_KEY, JSON.stringify(data)); } catch { /* ignore */ }
      })
      .catch(() => {});
  }, []);

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);

    try {
      await authApi.forgotPassword(forgotEmail);
      setForgotSent(true);
    } catch {
      setForgotError(t('auth.generic_error'));
    } finally {
      setForgotLoading(false);
    }
  };

  const handleBackFromForgot = () => {
    setForgotMode(false);
    setForgotEmail('');
    setForgotSent(false);
    setForgotError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await authApi.login(email, password);
      const data = response.data;

      // Si la 2FA est requise, afficher le formulaire de vérification
      if (data.requiresMFA) {
        const method = data.mfaMethod || 'TOTP';
        setMfaRequired(true);
        setMfaUserId(data.userId);
        setMfaToken(data.mfaToken || '');
        setMfaEmail(data.email);
        setMfaMethod(method);

        if (method === 'DUO') {
          // Redirect to Duo Universal Prompt
          setIsDuoRedirecting(true);
          try {
            const redirectUri = `${window.location.origin}/duo-callback`;
            const duoRes = await authApi.getDuoAuthUrl(data.mfaToken, redirectUri);
            // Store state in sessionStorage for the callback
            sessionStorage.setItem('duo_state', duoRes.data.state);
            sessionStorage.setItem('duo_redirect_uri', redirectUri);
            window.location.href = duoRes.data.authUrl;
          } catch {
            setError(t('auth.duo_failed'));
            setIsDuoRedirecting(false);
            setMfaRequired(false);
          }
          return;
        }

        setMfaCode(['', '', '', '', '', '']);
        setTimeout(() => mfaInputRefs.current[0]?.focus(), 100);
        return;
      }

      const { user, accessToken } = data;
      setAuth(user, accessToken);
      await applyUserLocale(i18n, user.language);

      // Redirect based on onboarding state
      if (user.role !== 'SUPER_ADMIN' && !user.cguAccepted) {
        navigate('/accept-cgu');
      } else if (user.role !== 'SUPER_ADMIN' && user.mustSetup2FA) {
        navigate('/setup-2fa');
      } else {
        navigate(isDesktop ? '/dashboard' : '/map');
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || t('auth.login_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = mfaCode.join('');
    if (code.length !== 6) return;

    setError('');
    setIsLoading(true);

    try {
      const response = await authApi.validate2FA(mfaUserId, code, mfaToken);
      const { user, accessToken } = response.data;

      setAuth(user, accessToken);
      await applyUserLocale(i18n, user.language);

      // Redirect based on onboarding state
      if (user.role !== 'SUPER_ADMIN' && !user.cguAccepted) {
        navigate('/accept-cgu');
      } else if (user.role !== 'SUPER_ADMIN' && user.mustSetup2FA) {
        navigate('/setup-2fa');
      } else {
        navigate(isDesktop ? '/dashboard' : '/map');
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || t('auth.code_invalid'));
      setMfaCode(['', '', '', '', '', '']);
      setTimeout(() => mfaInputRefs.current[0]?.focus(), 100);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaInput = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...mfaCode];
    newCode[index] = value.slice(-1);
    setMfaCode(newCode);

    // Auto-focus le champ suivant
    if (value && index < 5) {
      mfaInputRefs.current[index + 1]?.focus();
    }
  };

  const handleMfaKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !mfaCode[index] && index > 0) {
      mfaInputRefs.current[index - 1]?.focus();
    }
  };

  const handleMfaPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 0) return;
    const newCode = [...mfaCode];
    for (let i = 0; i < pasted.length; i++) {
      newCode[i] = pasted[i];
    }
    setMfaCode(newCode);
    const nextIndex = Math.min(pasted.length, 5);
    mfaInputRefs.current[nextIndex]?.focus();
  };

  const handleBackToLogin = () => {
    setMfaRequired(false);
    setMfaUserId('');
    setMfaEmail('');
    setMfaMethod('TOTP');
    setMfaCode(['', '', '', '', '', '']);
    setError('');
    setIsDuoRedirecting(false);
  };

  const primaryColor = appearance.primaryColor;

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900" />
        <div
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse-slow"
          style={{ backgroundColor: `${primaryColor}33` }}
        />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-purple/20 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />
        <div className="relative z-10 flex flex-col justify-center p-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 rounded-2xl overflow-hidden shadow-glow">
              {appearance.logo ? (
                <img src={appearance.logo} alt={appearance.companyName} className="w-full h-full object-cover" />
              ) : (
                <img src="/logo.png" alt="TrackFleet" className="w-full h-full object-cover" />
              )}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">{appearance.companyName}</h1>
              <p className="text-dark-400">{appearance.companyTagline || 'GPS Fleet Tracking'}</p>
            </div>
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">
            {t('auth.marketing_headline_1')}
            <br />
            <span style={{ color: primaryColor }}>{t('auth.marketing_headline_2')}</span>
          </h2>
          <p className="text-dark-200 text-lg max-w-md mb-8">
            {t('auth.marketing_subtitle')}
          </p>
          <div className="grid grid-cols-2 gap-4 max-w-md">
            <FeatureItem icon="satellite" text={t('auth.feature_realtime')} color={primaryColor} />
            <FeatureItem icon="route" text={t('auth.feature_history')} color={primaryColor} />
            <FeatureItem icon="alert" text={t('auth.feature_alerts')} color={primaryColor} />
            <FeatureItem icon="report" text={t('auth.feature_reports')} color={primaryColor} />
          </div>
        </div>
      </div>

      {/* Right side - Login form / 2FA form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl overflow-hidden shadow-lg">
                {appearance.logo ? (
                  <img src={appearance.logo} alt={appearance.companyName} className="w-full h-full object-cover" />
                ) : (
                  <img src="/logo.png" alt="TrackFleet" className="w-full h-full object-cover" />
                )}
              </div>
              <span className="text-2xl font-bold text-gray-900">{appearance.companyName}</span>
            </div>
          </div>

          {forgotMode ? (
            /* ---- Formulaire mot de passe oublié ---- */
            <>
              <div className="text-center mb-8">
                <div className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${primaryColor}15` }}>
                  <Mail className="h-8 w-8" style={{ color: primaryColor }} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.forgot_title')}</h2>
                <p className="text-gray-500 text-sm">
                  {forgotSent ? t('auth.forgot_subtitle_sent') : t('auth.forgot_subtitle_initial')}
                </p>
              </div>

              {forgotSent ? (
                <div className="space-y-6">
                  <div className="p-4 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                    <span dangerouslySetInnerHTML={{ __html: t('auth.forgot_confirmation', { email: forgotEmail }) }} />
                  </div>
                  <button
                    type="button"
                    onClick={handleBackFromForgot}
                    className="w-full flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {t('auth.back_to_login')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotSubmit} className="space-y-6">
                  {forgotError && (
                    <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                      {forgotError}
                    </div>
                  )}

                  <div>
                    <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('auth.email')}
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        id="forgot-email"
                        type="email"
                        autoComplete="email"
                        required
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 transition-colors"
                        onFocus={(e) => e.target.style.borderColor = primaryColor}
                        onBlur={(e) => e.target.style.borderColor = ''}
                        placeholder={t('auth.email_placeholder')}
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={forgotLoading || !forgotEmail}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: primaryColor }}
                    onMouseOver={(e) => !forgotLoading && (e.currentTarget.style.filter = 'brightness(0.9)')}
                    onMouseOut={(e) => e.currentTarget.style.filter = ''}
                  >
                    {forgotLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        {t('auth.send_link')}
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleBackFromForgot}
                    className="w-full flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {t('auth.back_to_login')}
                  </button>
                </form>
              )}
            </>
          ) : isDuoRedirecting ? (
            /* ---- Duo redirect loading ---- */
            <>
              <div className="text-center mb-8">
                <div className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${primaryColor}15` }}>
                  <ShieldCheck className="h-8 w-8" style={{ color: primaryColor }} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.duo_title')}</h2>
                <p className="text-gray-500 text-sm">{t('auth.duo_subtitle')}</p>
                <p className="text-gray-400 text-xs mt-1">{mfaEmail}</p>
              </div>
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" style={{ color: primaryColor }} />
              </div>
              <button
                type="button"
                onClick={handleBackToLogin}
                className="w-full flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Retour à la connexion
              </button>
            </>
          ) : mfaRequired ? (
            /* ---- Formulaire 2FA ---- */
            <>
              <div className="text-center mb-8">
                <div className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${primaryColor}15` }}>
                  <ShieldCheck className="h-8 w-8" style={{ color: primaryColor }} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.two_factor_title')}</h2>
                <p className="text-gray-500 text-sm">{t('auth.two_factor_subtitle')}</p>
                <p className="text-gray-400 text-xs mt-1">{mfaEmail}</p>
              </div>

              <form onSubmit={handleMfaSubmit} className="space-y-6">
                {error && (
                  <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                    {error}
                  </div>
                )}

                <div className="flex justify-center gap-3" onPaste={handleMfaPaste}>
                  {mfaCode.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => { mfaInputRefs.current[index] = el; }}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleMfaInput(index, e.target.value)}
                      onKeyDown={(e) => handleMfaKeyDown(index, e)}
                      className="w-12 h-14 text-center text-xl font-bold bg-white border-2 border-gray-300 rounded-xl text-gray-900 transition-colors focus:outline-none"
                      style={{ borderColor: digit ? primaryColor : undefined }}
                      onFocus={(e) => e.target.style.borderColor = primaryColor}
                      onBlur={(e) => { if (!digit) e.target.style.borderColor = ''; }}
                    />
                  ))}
                </div>

                <button
                  type="submit"
                  disabled={isLoading || mfaCode.join('').length !== 6}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: primaryColor }}
                  onMouseOver={(e) => !isLoading && (e.currentTarget.style.filter = 'brightness(0.9)')}
                  onMouseOut={(e) => e.currentTarget.style.filter = ''}
                >
                  {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      {t('auth.verify')}
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="w-full flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Retour à la connexion
                </button>
              </form>
            </>
          ) : (
            /* ---- Formulaire de connexion classique ---- */
            <>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.welcome')}</h2>
                <p className="text-gray-500">{t('auth.welcome_subtitle')}</p>
              </div>

              {supportsPasskey && (
                <div className="mb-6">
                  <button
                    type="button"
                    onClick={handlePasskeyLogin}
                    disabled={passkeyLoading}
                    className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-white border-2 border-gray-200 text-gray-700 font-medium rounded-xl transition-all hover:border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {passkeyLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Fingerprint className="h-5 w-5" style={{ color: primaryColor }} />
                    )}
                    {t('auth.passkey_button')}
                  </button>

                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-gray-50 text-gray-400">{t('auth.or')}</span>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                    {error}
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('auth.email')}
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 transition-colors"
                        style={{
                          borderColor: email ? primaryColor : undefined,
                        }}
                        onFocus={(e) => e.target.style.borderColor = primaryColor}
                        onBlur={(e) => e.target.style.borderColor = ''}
                        placeholder={t('auth.email_placeholder')}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('auth.password')}
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        id="password"
                        name="password"
                        type={passwordReveal.inputProps.type}
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={passwordReveal.inputProps.onChange}
                        onKeyDown={passwordReveal.inputProps.onKeyDown}
                        onBeforeInput={passwordReveal.inputProps.onBeforeInput}
                        onPaste={passwordReveal.inputProps.onPaste}
                        className="w-full pl-12 pr-12 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 transition-colors"
                        onFocus={(e) => e.target.style.borderColor = primaryColor}
                        onBlur={(e) => e.target.style.borderColor = ''}
                        placeholder="••••••••"
                      />
                      {passwordReveal.canReveal && (
                        <button
                          type="button"
                          onClick={passwordReveal.toggle}
                          aria-label={passwordReveal.visible ? t('auth.hide_password') : t('auth.show_password')}
                          aria-pressed={passwordReveal.visible}
                          title={passwordReveal.visible ? t('auth.hide_password') : t('auth.show_password')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                        >
                          {passwordReveal.visible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center gap-2 text-gray-600">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-gray-300 bg-white"
                      style={{ accentColor: primaryColor }}
                    />
                    {t('auth.remember_me')}
                  </label>
                  <button
                    type="button"
                    onClick={() => { setForgotMode(true); setForgotEmail(email); setError(''); }}
                    className="transition-colors"
                    style={{ color: primaryColor }}
                    onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'}
                    onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    {t('auth.forgot_password')}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: primaryColor }}
                  onMouseOver={(e) => !isLoading && (e.currentTarget.style.filter = 'brightness(0.9)')}
                  onMouseOut={(e) => e.currentTarget.style.filter = ''}
                >
                  {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      {t('auth.sign_in')}
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </form>

              {appearance.showRegistration !== false && (
                <p className="text-center text-sm text-gray-500 mt-6">
                  {t('auth.no_account_yet')}{' '}
                  <Link
                    to="/register"
                    className="font-medium transition-colors"
                    style={{ color: primaryColor }}
                  >
                    {t('auth.register_link')}
                  </Link>
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FeatureItem({ icon, text, color }: { icon: string; text: string; color: string }) {
  const getIcon = () => {
    switch (icon) {
      case 'satellite':
        return (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
        );
      case 'route':
        return (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
        );
      case 'alert':
        return (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        );
      case 'report':
        return (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5">
      <div style={{ color }}>{getIcon()}</div>
      <span className="text-sm text-dark-200">{text}</span>
    </div>
  );
}
